import { useEffect, useRef, useState } from "react";
import {
  MotionConfig,
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { ScrollProgress } from "@/components/ScrollProgress";
import { FarmStickyBar } from "@/components/FarmStickyBar";
import { FarmhouseSheet } from "@/components/FarmhouseSheet";
import { Eyebrow } from "@/components/bits";
import { SITE } from "@/siteConfig";
import { useLang } from "@/i18n";

const OFFERS = ["weekend", "plantation", "river", "coast", "else"];
const STEPS = [1, 2, 3, 4];
const MQ = ["fh.mq1", "fh.mq2", "fh.mq3", "fh.mq4", "fh.mq5", "fh.mq6"];
const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Rise = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const Chapter = ({ num, title }) => (
  <div className="flex items-center gap-4">
    <span className="font-display italic text-2xl text-[#B8894B]">{num}</span>
    <span className="h-px flex-1 bg-[#1A2A24]" />
    <span className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#EDE7DA]/45">
      {title}
    </span>
  </div>
);

export default function FarmhousesPage() {
  const { t } = useLang();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [intent, setIntent] = useState("");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  useEffect(() => {
    const setMeta = (sel, val) => {
      const el = document.head.querySelector(sel);
      if (el) el.setAttribute("content", val);
    };
    setMeta("meta[property=\"og:title\"]", "Farmhouses & estates in Odisha — Novare Estates");
    setMeta("meta[property=\"og:image\"]", `${window.location.origin}/og-buyers.png`);
    setMeta("meta[name=\"twitter:image\"]", `${window.location.origin}/og-buyers.png`);
  }, []);

  const openSheet = (intentKey = "") => {
    setIntent(intentKey);
    setSheetOpen(true);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="bg-[#0E1613] text-[#EDE7DA] min-h-screen">
        <ScrollProgress />
        <Nav onBookCall={() => openSheet()} />

        <div className="mx-auto w-full max-w-[480px] md:border-x md:border-[#1A2A24] pb-28">
          {/* ——— hero ——— */}
          <section
            ref={heroRef}
            data-testid="farm-hero"
            className="relative overflow-hidden px-6 pt-32 pb-12"
          >
            <motion.div
              aria-hidden
              style={{ y: glowY }}
              className="pointer-events-none absolute -top-44 left-1/2 -translate-x-1/2 h-[440px] w-[440px] rounded-full bg-[#B8894B]/10 blur-[110px]"
            />
            <div className="relative">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, ease: EASE }}
              >
                <Eyebrow>{t("fh.eyebrow")}</Eyebrow>
              </motion.div>
              <h1 className="mt-7 font-display text-4xl leading-[1.06] tracking-tight text-[#EDE7DA]">
                <MaskLine delay={0.2}>{t("fh.h1a")}</MaskLine>
                <MaskLine delay={0.34}>
                  <em className="text-[#B8894B]">{t("fh.h1em")}</em>
                </MaskLine>
              </h1>
              <motion.p
                data-testid="farm-hero-sub"
                className="mt-6 text-sm font-light leading-relaxed text-[#EDE7DA]/60"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
              >
                {t("fh.sub")}
              </motion.p>
              <motion.button
                type="button"
                data-testid="farm-hero-cta"
                onClick={() => openSheet()}
                className="mt-8 h-14 w-full bg-[#B8894B] text-[#0E1613] font-mono-label text-xs tracking-[0.2em] uppercase active:bg-[#EDE7DA] transition-colors duration-200 flex items-center justify-center gap-2"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                whileTap={{ scale: 0.98 }}
              >
                {t("fh.cta")}
                <ArrowUpRight className="w-4 h-4" />
              </motion.button>
            </div>
          </section>

          {/* ——— value chips ——— */}
          <Rise className="px-6 pb-2">
            <div className="grid gap-2" data-testid="farm-chips">
              {["fh.chip1", "fh.chip2", "fh.chip3"].map((k, i) => (
                <p
                  key={k}
                  data-testid={`farm-chip-${i + 1}`}
                  className="flex items-center gap-3 border border-[#1A2A24] bg-[#131F1A] px-4 min-h-[44px] font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#EDE7DA]/70"
                >
                  <span className="h-1 w-1 bg-[#B8894B]" />
                  {t(k)}
                </p>
              ))}
            </div>
          </Rise>

          {/* ——— editorial marquee ——— */}
          <div
            data-testid="farm-marquee"
            className="marquee marquee-slow border-y border-[#1A2A24] mt-10 py-4"
          >
            <div className="marquee-track">
              {[0, 1].map((dup) => (
                <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
                  {MQ.map((k) => (
                    <span
                      key={`${dup}-${k}`}
                      className="flex items-center font-display italic text-lg text-[#EDE7DA]/45"
                    >
                      <span className="px-5">{t(k)}</span>
                      <span className="h-1 w-1 bg-[#B8894B]/60" />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* ——— 01 · what we can get you ——— */}
          <section data-testid="farm-offers" className="px-6 pt-14">
            <Rise>
              <Chapter num="01" title={t("fh.offers.tag")} />
            </Rise>
            <div className="mt-8">
              {OFFERS.map((key, i) => (
                <Rise key={key} delay={i * 0.05}>
                  <button
                    type="button"
                    data-testid={`farm-offer-${key}`}
                    onClick={() => openSheet(`fh.opt.${key}`)}
                    className="group w-full text-left border-b border-[#1A2A24] py-5 min-h-[72px] flex items-center justify-between gap-4 active:bg-[#131F1A] transition-colors duration-200"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono-label text-[10px] tracking-[0.2em] text-[#B8894B]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>
                        <span className="block font-display text-xl tracking-tight text-[#EDE7DA]">
                          {t(`fh.opt.${key}`)}
                        </span>
                        <span className="mt-1 block text-xs font-light leading-relaxed text-[#EDE7DA]/50">
                          {t(`fh.opt.${key}.n`)}
                        </span>
                      </span>
                    </span>
                    <ArrowUpRight className="w-4 h-4 shrink-0 text-[#EDE7DA]/35 group-active:text-[#B8894B] transition-colors duration-200" />
                  </button>
                </Rise>
              ))}
            </div>
          </section>

          {/* ——— 02 · how it works ——— */}
          <section data-testid="farm-steps" className="px-6 pt-14">
            <Rise>
              <Chapter num="02" title={t("fh.steps.tag")} />
            </Rise>
            <div className="mt-8 grid gap-8">
              {STEPS.map((n) => (
                <Rise key={n} delay={n * 0.06}>
                  <div data-testid={`farm-step-${n}`} className="flex gap-5">
                    <span className="font-display italic text-3xl leading-none text-[#B8894B]/70">
                      {String(n).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-display text-lg tracking-tight text-[#EDE7DA]">
                        {t(`fh.s${n}.t`)}
                      </h3>
                      <p className="mt-1.5 text-sm font-light leading-relaxed text-[#EDE7DA]/55">
                        {t(`fh.s${n}.c`)}
                      </p>
                    </div>
                  </div>
                </Rise>
              ))}
            </div>
          </section>

          {/* ——— 03 · final CTA ——— */}
          <section data-testid="farm-final" className="px-6 pt-16 pb-14">
            <Rise>
              <Chapter num="03" title="" />
            </Rise>
            <Rise delay={0.08}>
              <h2 className="mt-8 font-display text-3xl leading-[1.1] tracking-tight text-[#EDE7DA]">
                {t("fh.final.a")}{" "}
                <em className="text-[#B8894B]">{t("fh.final.em")}</em>
              </h2>
              <p className="mt-4 text-sm font-light leading-relaxed text-[#EDE7DA]/55">
                {t("fh.final.sub")}
              </p>
              <button
                type="button"
                data-testid="farm-final-cta"
                onClick={() => openSheet()}
                className="mt-8 h-14 w-full border border-[#B8894B] text-[#B8894B] font-mono-label text-xs tracking-[0.2em] uppercase active:bg-[#B8894B] active:text-[#0E1613] transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {t("fh.cta")}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </Rise>
          </section>

          {/* ——— minimal footer ——— */}
          <footer data-testid="farm-footer" className="border-t border-[#1A2A24] px-6 py-10">
            <div className="flex items-center gap-3">
              <img src="/logo-mark.png" alt="Novare Estates monogram" className="h-9 w-9" />
              <p className="font-display text-lg tracking-tight text-[#EDE7DA]">
                NOVARE ESTATES
              </p>
            </div>
            <p className="mt-3 font-mono-label text-[9px] tracking-[0.28em] uppercase text-[#B8894B]">
              {t("nav.tagline")}
            </p>
            <p className="mt-6 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#EDE7DA]/35">
              {SITE.city}
            </p>
          </footer>
        </div>

        <FarmStickyBar onOpen={() => openSheet()} />
        <FarmhouseSheet
          open={sheetOpen}
          intent={intent}
          onClose={() => setSheetOpen(false)}
        />
      </div>
    </MotionConfig>
  );
}
